import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../hooks';
import { confirmPasswordReset, clearPasswordResetStatus } from '../store/slices/authSlice';

const schema = yup.object({
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .matches(/(?=.*[a-z])/, 'Password must contain at least one lowercase letter')
    .matches(/(?=.*[A-Z])/, 'Password must contain at least one uppercase letter')
    .matches(/(?=.*\d)/, 'Password must contain at least one number')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords do not match')
    .required('Please confirm your password'),
}).required();

type FormData = {
  password: string;
  confirmPassword: string;
};

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error, passwordResetStatus } = useAppSelector((state) => state.auth);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
    }
    return () => {
      dispatch(clearPasswordResetStatus());
    };
  }, [token, navigate, dispatch]);

  const onSubmit = async (data: FormData) => {
    if (!token) {
      return;
    }

    try {
      await dispatch(confirmPasswordReset({
        token,
        password: data.password
      })).unwrap();
    } catch (err: any) {
      console.error('Password reset failed:', err);
      if (err.message?.includes('expired') || err.message?.includes('invalid')) {
        setTokenValid(false);
      }
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const handleRequestNewReset = () => {
    navigate('/forgot-password');
  };

  if (!tokenValid) {
    return (
      <div style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#000000',
        position: 'relative'
      }}>
        <div style={{
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem 1rem'
        }}>
          <div style={{
            width: '440px',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}>
              <img
                src="/images/ana_logo.png"
                alt="ANA Logo"
                style={{ height: '48px', width: '48px' }}
              />
              <img
                src="/images/ana_text_dark.svg"
                alt="ANA"
                style={{ marginTop: '1rem', height: '32px', width: 'auto' }}
              />
              <h1 style={{
                marginTop: '1.5rem',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#FFFFFF',
                marginBottom: 0
              }}>
                Invalid Reset Link
              </h1>
            </div>

            <div style={{
              background: 'rgba(15, 15, 15, 0.6)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '16px',
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ef4444'
              }}>
                <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              
              <div>
                <h2 style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  margin: 0
                }}>
                  Link Invalid
                </h2>
                <p style={{
                  color: '#9CA3AF',
                  margin: '1rem 0 0 0',
                  lineHeight: 1.6
                }}>
                  Invalid password reset link. No token provided.
                </p>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                width: '100%'
              }}>
                <button
                  onClick={handleRequestNewReset}
                  style={{
                    width: '100%',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#FF4C00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#CC3D00'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#FF4C00'}
                >
                  Request New Reset Link
                </button>
                <button
                  onClick={handleLoginRedirect}
                  style={{
                    width: '100%',
                    padding: '0.5rem 1rem',
                    background: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '1.5rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: '#9CA3AF'
          }}>
            Built with ❤️ by <a href="https://www.openana.ai/" target="_blank" rel="noopener noreferrer" style={{
              color: '#FF4C00',
              textDecoration: 'none',
              transition: 'color 0.2s ease'
            }}>Ana</a>
          </div>
        </div>
      </div>
    );
  }

  if (passwordResetStatus) {
    return (
      <div style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#000000',
        position: 'relative'
      }}>
        <div style={{
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem 1rem'
        }}>
          <div style={{
            width: '440px',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}>
              <img
                src="/images/ana_logo.png"
                alt="ANA Logo"
                style={{ height: '48px', width: '48px' }}
              />
              <img
                src="/images/ana_text_dark.svg"
                alt="ANA"
                style={{ marginTop: '1rem', height: '32px', width: 'auto' }}
              />
              <h1 style={{
                marginTop: '1.5rem',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#FFFFFF',
                marginBottom: 0
              }}>
                Password Reset Successful
              </h1>
            </div>

            <div style={{
              background: 'rgba(15, 15, 15, 0.6)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '16px',
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.05))',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#22c55e'
              }}>
                <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <div>
                <h2 style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  margin: 0
                }}>
                  Password Updated!
                </h2>
                <p style={{
                  color: '#9CA3AF',
                  margin: '1rem 0 0 0',
                  lineHeight: 1.6
                }}>
                  Your password has been successfully reset. You can now log in with your new password.
                </p>
              </div>

              <div style={{ width: '100%' }}>
                <button
                  onClick={handleLoginRedirect}
                  style={{
                    width: '100%',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#FF4C00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#CC3D00'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#FF4C00'}
                >
                  Continue to Login
                </button>
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '1.5rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: '#9CA3AF'
          }}>
            Built with ❤️ by <a href="https://www.openana.ai/" target="_blank" rel="noopener noreferrer" style={{
              color: '#FF4C00',
              textDecoration: 'none',
              transition: 'color 0.2s ease'
            }}>Ana</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#000000',
      position: 'relative'
    }}>
      <div style={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem'
      }}>
        <div style={{
          width: '440px',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            <img
              src="/images/ana_logo.png"
              alt="ANA Logo"
              style={{ height: '48px', width: '48px' }}
            />
            <img
              src="/images/ana_text_dark.svg"
              alt="ANA"
              style={{ marginTop: '1rem', height: '32px', width: 'auto' }}
            />
            <h1 style={{
              marginTop: '1.5rem',
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#FFFFFF',
              marginBottom: 0
            }}>
              Reset Password
            </h1>
            <p style={{
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              color: '#9CA3AF',
              marginBottom: 0
            }}>
              Enter your new password
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                color: '#9CA3AF',
                marginBottom: '0.25rem'
              }}>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  {...register('password')}
                  type={showNewPassword ? "text" : "password"}
                  disabled={isLoading}
                  placeholder="Enter your new password"
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    paddingRight: '2.5rem',
                    backgroundColor: 'rgba(15, 15, 15, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: '#FFFFFF',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#FF4C00'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)'}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#9CA3AF',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showNewPassword ? (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <p style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#ef4444' }}>
                  {errors.password.message}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                color: '#9CA3AF',
                marginBottom: '0.25rem'
              }}>
                Confirm New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  {...register('confirmPassword')}
                  type={showConfirmPassword ? "text" : "password"}
                  disabled={isLoading}
                  placeholder="Confirm your new password"
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    paddingRight: '2.5rem',
                    backgroundColor: 'rgba(15, 15, 15, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: '#FFFFFF',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#FF4C00'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)'}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#9CA3AF',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showConfirmPassword ? (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#ef4444' }}>
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <div style={{
              fontSize: '0.75rem',
              color: '#9CA3AF',
              marginTop: '0.5rem'
            }}>
              Password must be at least 8 characters long and contain uppercase, lowercase, and numeric characters.
            </div>

            {error && (
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.5rem 1rem',
                backgroundColor: '#FF4C00',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                opacity: isLoading ? 0.5 : 1
              }}
              onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#CC3D00')}
              onMouseOut={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#FF4C00')}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={handleLoginRedirect}
              style={{
                background: 'none',
                border: 'none',
                color: '#FF4C00',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textDecoration: 'none',
                transition: 'color 0.2s ease'
              }}
            >
              Back to Login
            </button>
          </div>
        </div>

        <div style={{
          marginTop: '1.5rem',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: '#9CA3AF'
        }}>
          Built with ❤️ by <a href="https://www.openana.ai/" target="_blank" rel="noopener noreferrer" style={{
            color: '#FF4C00',
            textDecoration: 'none',
            transition: 'color 0.2s ease'
          }}>Ana</a>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ResetPassword;
